import { useEffect, useState } from "react";
import { Boxes, Save, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function StockAdmin() {
  const [stock, setStock] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/stock").then((r) => setStock(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  if (!stock) return <p className="text-muted-foreground">Chargement…</p>;

  const available = stock.total - stock.reserved - stock.sold;
  const alert = available <= stock.threshold;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { total: Number(stock.total) || 0, reserved: Number(stock.reserved) || 0, sold: Number(stock.sold) || 0, threshold: Number(stock.threshold) || 0 };
      const { data } = await api.put("/admin/stock", payload);
      setStock(data);
      toast.success("Stock enregistré");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-stock" className="max-w-3xl">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx mb-8">Stock</h1>

      {alert && (
        <div className="mb-6 flex items-center gap-3 bg-rouge text-white p-4 text-sm font-semibold" data-testid="stock-alert-banner">
          <TriangleAlert className="w-5 h-5 shrink-0" />
          {available <= 0 ? "Rupture de stock !" : `Stock bas : ${available} boîte(s) disponible(s) (seuil : ${stock.threshold}).`}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stock-cards">
        {[
          { label: "Stock total", value: stock.total, id: "total" },
          { label: "Disponible", value: available, id: "available", highlight: alert },
          { label: "Réservé", value: stock.reserved, id: "reserved" },
          { label: "Vendu", value: stock.sold, id: "sold" },
        ].map((c) => (
          <div key={c.id} className={`bg-white border p-5 ${c.highlight ? "border-rouge" : "border-line"}`} data-testid={`stock-${c.id}`}>
            <Boxes className={`w-5 h-5 mb-3 ${c.highlight ? "text-rouge" : "text-muted-foreground"}`} />
            <p className="font-display text-3xl font-extrabold text-onyx">{c.value}</p>
            <p className="text-xs text-muted-foreground mt-1 font-medium">{c.label}</p>
          </div>
        ))}
      </div>

      <section className="bg-white border border-line p-6 sm:p-8 mb-6">
        <h2 className="font-display font-bold text-lg text-onyx mb-5">Ajuster le stock</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label>Stock total (boîtes)</Label>
            <Input type="number" min="0" className="mt-1.5" data-testid="stock-total-input" value={stock.total} onChange={(e) => setStock({ ...stock, total: e.target.value })} />
          </div>
          <div>
            <Label>Stock réservé</Label>
            <Input type="number" min="0" className="mt-1.5" data-testid="stock-reserved-input" value={stock.reserved} onChange={(e) => setStock({ ...stock, reserved: e.target.value })} />
          </div>
          <div>
            <Label>Stock vendu (ajusté automatiquement par les commandes)</Label>
            <Input type="number" min="0" className="mt-1.5" data-testid="stock-sold-input" value={stock.sold} onChange={(e) => setStock({ ...stock, sold: e.target.value })} />
          </div>
          <div>
            <Label>Seuil d'alerte</Label>
            <Input type="number" min="0" className="mt-1.5" data-testid="stock-threshold-input" value={stock.threshold} onChange={(e) => setStock({ ...stock, threshold: e.target.value })} />
          </div>
        </div>
      </section>

      <p className="text-sm text-muted-foreground mb-6 bg-sand p-4">
        À l'activation de la vente en ligne (phase 2), chaque commande payée décrémentera automatiquement le stock
        et les commandes dépassant le stock disponible seront bloquées. Les commandes manuelles sont déjà contrôlées.
      </p>

      <button onClick={save} disabled={saving} data-testid="stock-save"
        className="inline-flex items-center gap-2 bg-rouge text-white px-8 py-3.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
        <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </div>
  );
}
